import seoData from '../data/seo.json';

/**
 * Get SEO data for a specific page
 * @param {string} pageName - The name of the page
 * @returns {Object} SEO data object with metaTitle, metaDescription, url, and canonical
 */
export const getSEOData = (pageName) => {
  // Ensure seoData is available
  if (!seoData || !Array.isArray(seoData)) {
    console.warn('SEO data not available, using default');
    return {
      metaTitle: "Baashyaam Group - Luxury Apartments in Chennai",
      metaDescription: "Discover luxury living in Chennai with Baashyaam Group. Premium residential and commercial projects with innovative architecture and unmatched lifestyle experience.",
      url: "https://www.bashyamgroup.com/",
      canonical: "https://bashyamgroup.com/"
    };
  }

  // First try exact match
  let seoInfo = seoData.find(item => 
    item.slug && item.slug.toLowerCase() === pageName.toLowerCase()
  );
  
  // console.log('getSEOData lookup:', { 
  //   pageName, 
  //   seoInfo: seoInfo?.page || 'not found',
  //   seoDataLength: seoData.length,
  //   availableSlugs: seoData.map(item => item.slug).slice(0, 5)
  // });
  
  // If no exact match, try partial match
  if (!seoInfo) {
    seoInfo = seoData.find(item => 
      item.slug && (
        item.slug.toLowerCase().includes(pageName.toLowerCase()) ||
        pageName.toLowerCase().includes(item.slug.toLowerCase())
      )
    );
    // console.log('Partial match result:', { pageName, seoInfo: seoInfo?.page || 'not found' });
  }
  
  // Return default SEO data if page not found
  if (!seoInfo) {
    console.warn(`SEO data not found for page: ${pageName}. Using default SEO data.`);
    return {
      metaTitle: "Baashyaam Group - Luxury Apartments in Chennai",
      metaDescription: "Discover luxury living in Chennai with Baashyaam Group. Premium residential and commercial projects with innovative architecture and unmatched lifestyle experience.",
      url: "https://www.bashyamgroup.com/",
      canonical: "https://bashyamgroup.com/"
    };
  }
  
  return seoInfo;
};

/**
 * Get SEO data based on current pathname
 * @param {string} pathname - The current pathname (e.g., '/about', '/contact')
 * @returns {Object} SEO data object
 */
export const getSEODataByPath = (pathname) => {
  // Remove leading slash and trailing slash, handle root path
  const cleanPath = pathname === '/' ? 'home' : pathname.replace(/^\/+|\/+$/g, '');
  
  // console.log('getSEODataByPath called with pathname:', pathname, 'cleanPath:', cleanPath);
  
  // Map pathnames to slugs
  const pathToPageMap = {
    'home': 'home',
    'about-iconic-residences-in-chennai': 'about-iconic-residences-in-chennai',
    'contact-luxury-apartments-chennai': 'contact-luxury-apartments-chennai',
    'ongoing-projects': 'ongoing-projects',
    'residential-completed-projects': 'residential-completed-projects',
    'commercial-ongoing-projects': 'commercial-ongoing-projects',
    'commercial-completed-projects': 'commercial-completed-projects',
    'project-gallery': 'project-gallery',
    'media': 'media-press',
    'crown-residence-luxury-apartments-koyambedu-chennai': 'crown-residence-luxury-apartments-koyambedu-chennai',
    'the-peak-high-rise-apartments-in-ashok-nagar': 'the-peak-high-rise-apartments-in-ashok-nagar',
    'promenade-commercial-guindy-chennai': 'promenade-commercial-guindy-chennai',
    'the-pinnacle-ra-puram-chennai': 'the-pinnacle-ra-puram-chennai',
    'royal-damera-gardens-poes-garden': 'royal-damera-gardens-poes-garden',
    'willow-square-commercial-guindy-chennai': 'willow-square-commercial-guindy-chennai',
    'srinivasa-west-mambalam-chennai': 'srinivasa-west-mambalam-chennai',
    'enchanted-luxury-apartments-in-thoraipakkam-chennai': 'enchanted-luxury-apartments-in-thoraipakkam-chennai',
    'joint-venture': 'joint-venture',
    'project-status': 'project-status',
    'privacy-policy': 'privacy-policy',
    'terms-and-conditions': 'terms-and-conditions',
    'the-plutus-residence-chennai': 'the-plutus-residence-chennai',
    'cloud-graze-luxury-apartments-kk-nagar': 'cloud-graze-luxury-apartments-kk-nagar',
    'le-grand-trianon-poes-garden-teynampet-chennai': 'le-grand-trianon-poes-garden-teynampet-chennai',
    'bonaventura': 'bonaventura-chennai',
    'sadhr-gardens': 'sadhr-gardens-chennai',
    'pinnacle-crest-sholinganallur': 'pinnacle-crest-sholinganallur',
    'blog-luxury-apartments': 'blog-luxury-apartments'
  };
  
  const pageName = pathToPageMap[cleanPath] || 'home';
  // console.log('Mapped to pageName:', pageName);
  return getSEOData(pageName);
};

/**
 * Generate structured data for the website
 * @param {Object} seoData - SEO data object
 * @returns {Object} Structured data object
 */
export const getStructuredData = (seoData) => {
  if (!seoData) {
    return {
      "@context": "https://schema.org",
      "@type": "RealEstateAgent",
      "name": "Baashyaam Group",
      "description": "Discover luxury living in Chennai with Baashyaam Group. Premium residential and commercial projects with innovative architecture and unmatched lifestyle experience.",
      "url": "https://bashyamgroup.com/",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Chennai",
        "addressCountry": "India"
      },
      "sameAs": [
        "https://www.facebook.com/baashyaamgroup",
        "https://www.instagram.com/baashyaamgroup",
        "https://www.linkedin.com/company/baashyaamgroup"
      ]
    };
  }
  
  return {
    "@context": "https://schema.org",
    "@type": "RealEstateAgent",
    "name": "Baashyaam Group",
    "description": seoData.metaDescription,
    "url": seoData.canonical,
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Chennai",
      "addressCountry": "India"
    },
    "sameAs": [
      "https://www.facebook.com/baashyaamgroup",
      "https://www.instagram.com/baashyaamgroup",
      "https://www.linkedin.com/company/baashyaamgroup"
    ]
  };
};

/**
 * Generate Open Graph meta tags
 * @param {Object} seoData - SEO data object
 * @returns {Object} Open Graph meta tags
 */
export const getOpenGraphTags = (seoData) => {
  if (!seoData) {
    return {
      'og:title': 'Baashyaam Group - Luxury Apartments in Chennai',
      'og:description': 'Discover luxury living in Chennai with Baashyaam Group. Premium residential and commercial projects with innovative architecture and unmatched lifestyle experience.',
      'og:url': 'https://bashyamgroup.com/',
      'og:type': 'website',
      'og:site_name': 'Baashyaam Group',
      'og:image': 'https://bashyamgroup.com/static/og-image.jpg',
      'og:image:width': '1200',
      'og:image:height': '630'
    };
  }
  
  return {
    'og:title': seoData.metaTitle,
    'og:description': seoData.metaDescription,
    'og:url': seoData.canonical,
    'og:type': 'website',
    'og:site_name': 'Baashyaam Group',
    'og:image': 'https://bashyamgroup.com/static/og-image.jpg',
    'og:image:width': '1200',
    'og:image:height': '630'
  };
};

/**
 * Generate Twitter Card meta tags
 * @param {Object} seoData - SEO data object
 * @returns {Object} Twitter Card meta tags
 */
export const getTwitterCardTags = (seoData) => {
  if (!seoData) {
    return {
      'twitter:card': 'summary_large_image',
      'twitter:title': 'Baashyaam Group - Luxury Apartments in Chennai',
      'twitter:description': 'Discover luxury living in Chennai with Baashyaam Group. Premium residential and commercial projects with innovative architecture and unmatched lifestyle experience.',
      'twitter:image': 'https://bashyamgroup.com/static/twitter-image.jpg'
    };
  }
  
  return {
    'twitter:card': 'summary_large_image',
    'twitter:title': seoData.metaTitle,
    'twitter:description': seoData.metaDescription,
    'twitter:image': 'https://bashyamgroup.com/static/twitter-image.jpg'
  };
};

/**
 * Generate standard HTML meta tags
 * @param {Object} seoData - SEO data object
 * @returns {Object} Standard HTML meta tags
 */
export const getMetaTags = (seoData) => {
  if (!seoData) {
    return {
      'title': 'Baashyaam Group - Luxury Apartments in Chennai',
      'description': 'Discover luxury living in Chennai with Baashyaam Group. Premium residential and commercial projects with innovative architecture and unmatched lifestyle experience.',
      'keywords': 'Baashyaam Group, luxury apartments Chennai, premium residential projects, commercial real estate Chennai, luxury homes, real estate developer Chennai',
      'author': 'Baashyaam Group',
      'robots': 'index, follow',
      'viewport': 'width=device-width, initial-scale=1.0',
      'charset': 'UTF-8',
      'language': 'en',
      'revisit-after': '7 days',
      'rating': 'general',
      'distribution': 'global',
      'canonical': 'https://bashyamgroup.com/',
      'url': 'https://www.bashyamgroup.com/',
      'theme-color': '#1a1a1a',
      'msapplication-TileColor': '#1a1a1a',
      'msapplication-config': '/browserconfig.xml'
    };
  }
  
  return {
    'title': seoData.metaTitle,
    'description': seoData.metaDescription,
    'keywords': 'Baashyaam Group, luxury apartments Chennai, premium residential projects, commercial real estate Chennai, luxury homes, real estate developer Chennai',
    'author': 'Baashyaam Group',
    'robots': 'index, follow',
    'viewport': 'width=device-width, initial-scale=1.0',
    'charset': 'UTF-8',
    'language': 'en',
    'revisit-after': '7 days',
    'rating': 'general',
    'distribution': 'global',
    'canonical': seoData.canonical,
    'url': seoData.url,
    'theme-color': '#1a1a1a',
    'msapplication-TileColor': '#1a1a1a',
    'msapplication-config': '/browserconfig.xml'
  };
};

/**
 * Generate all SEO meta tags (standard + Open Graph + Twitter Card)
 * @param {Object} seoData - SEO data object
 * @returns {Object} Combined meta tags object
 */
export const getAllMetaTags = (seoData) => {
  return {
    ...getMetaTags(seoData),
    ...getOpenGraphTags(seoData),
    ...getTwitterCardTags(seoData)
  };
};

/**
 * Validate SEO data object
 * @param {Object} seoData - SEO data object to validate
 * @returns {Object} Validation result with isValid and errors
 */
export const validateSEOData = (seoData) => {
  const errors = [];
  
  if (!seoData.metaTitle || seoData.metaTitle.length < 30) {
    errors.push('Meta title should be at least 30 characters long');
  }
  
  if (!seoData.metaDescription || seoData.metaDescription.length < 120) {
    errors.push('Meta description should be at least 120 characters long');
  }
  
  if (!seoData.canonical || !seoData.canonical.startsWith('http')) {
    errors.push('Canonical URL should be a valid HTTP/HTTPS URL');
  }
  
  if (seoData.metaTitle && seoData.metaTitle.length > 60) {
    errors.push('Meta title should be less than 60 characters for optimal SEO');
  }
  
  if (seoData.metaDescription && seoData.metaDescription.length > 160) {
    errors.push('Meta description should be less than 160 characters for optimal SEO');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Generate additional SEO meta tags for better search engine optimization
 * @param {Object} seoData - SEO data object
 * @returns {Object} Additional meta tags
 */
export const getAdditionalMetaTags = (seoData) => {
  return {
    'geo.region': 'IN-TN',
    'geo.placename': 'Chennai',
    'geo.position': '13.0827;80.2707',
    'ICBM': '13.0827, 80.2707',
    'DC.title': seoData.metaTitle,
    'DC.description': seoData.metaDescription,
    'DC.subject': 'Real Estate, Luxury Apartments, Chennai',
    'DC.creator': 'Baashyaam Group',
    'DC.publisher': 'Baashyaam Group',
    'DC.language': 'en',
    'DC.coverage': 'Chennai, Tamil Nadu, India',
    'DC.rights': '© 2024 Baashyaam Group. All rights reserved.',
    'format-detection': 'telephone=no',
    'mobile-web-app-capable': 'yes',
    'apple-mobile-web-app-capable': 'yes',
    'apple-mobile-web-app-status-bar-style': 'black-translucent',
    'apple-mobile-web-app-title': 'Baashyaam Group'
  };
};